import classNames from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { centsToDollarsNumber } from '../../../../lib/util';
import styles from './ProductTitle.module.scss';
import { useLocale } from '../../../../contexts/LocaleContext';

interface FlatPrice {
  priceInCents: number;
}

interface LowestSubscriptionBundlePrice {
  cheapestSubscriptionRate: string;
  cheapestSubscriptionRateUnit: string;
}

export type ProductTitlePrice = FlatPrice | LowestSubscriptionBundlePrice;

interface ProductTitleProps {
  productName: string;
  price: ProductTitlePrice;
  /**
   * Render the name exactly as written instead of the default uppercase treatment. Used by the Mini
   * Packs, whose relaunch naming is set in sentence case.
   */
  preserveNameCase?: boolean;
}

function priceIsBundlePrice(price: ProductTitlePrice): price is LowestSubscriptionBundlePrice {
  return 'cheapestSubscriptionRate' in price;
}

export default function ProductTitle({ productName, price, preserveNameCase = false }: ProductTitleProps) {
  const { t } = useTranslation();
  const { currencyFormatter } = useLocale();
  const priceCopy = priceIsBundlePrice(price)
    ? t('starting_at_rate_unit', { rate: price.cheapestSubscriptionRate, unit: price.cheapestSubscriptionRateUnit })
    : currencyFormatter.format(centsToDollarsNumber(price.priceInCents));

  return (
    <div className={styles.container}>
      <h2 className={classNames(styles.title, { [styles.preserveCase]: preserveNameCase })}>{productName}</h2>
      <span className={styles.price}>{priceCopy}</span>
    </div>
  );
}
